import { BasePlugin } from '../base-plugin';
import type {
  PluginContext,
  ParagraphTranslateConfig,
  TransInsertContext,
  RevertableMutator,
  TranslatingContext,
} from '../../types';
import { ViewportObserver } from '../../core/viewport-observer';
import { DOMChangeDetector } from '../../core/dom-change-detector';
import {
  getParagraphs,
  filterVisibleNodes,
  getHiddenAncestors,
  createTransNode,
  normalizeWhitespace,
  hashString,
  type Paragraph,
} from '../../utils/dom';
import { matchesParagraphSelectors } from '../../utils/paragraph-selector';
import { html2Text, text2HTML } from '../../utils/text';

const TRANS_NODE_TAG = 'TRANS';

export class ParagraphComparePlugin extends BasePlugin {
  name = 'paragraph-compare';
  private config!: ParagraphTranslateConfig;
  private initialized = false;
  private viewportObserver: ViewportObserver | null = null;
  private domChangeDetector: DOMChangeDetector | null = null;
  private translatedParagraphs = new Map<string, HTMLElement>();
  private pendingTranslations = new Set<string>();
  private rollbackStacks = new Map<string, Array<() => void>>();

  async init(ctx: PluginContext, config: ParagraphTranslateConfig): Promise<void> {
    this.ctx = ctx;
    this.config = config;
    this.initialized = true;

    await this.waitForReady(config.translateDelay);
    await this.translateAll();

    if (config.dynamic !== false) {
      this.startDynamicDetection();
    }
  }

  private async translateAll(): Promise<void> {
    if (this.destroyed) return;
    const targets = this.getTargets();
    const tasks: Promise<void>[] = [];

    for (const target of targets) {
      const excludeSelector = this.buildExcludeSelector();
      const paragraphs = getParagraphs(target, excludeSelector);

      for (const para of paragraphs) {
        if (this.isAlreadyTranslated(para)) continue;

        const textNodes = para.flatNodes.filter(
          n => n.nodeType === Node.TEXT_NODE,
        ) as Text[];

        const { visible, hidden } = filterVisibleNodes(textNodes);

        if (visible.length > 0) {
          if (this.config.lazyload) {
            this.observeForLazy(para);
          } else {
            tasks.push(this.translateParagraph(para));
          }
        } else if (hidden.length > 0) {
          this.observeHidden(para, hidden);
        }
      }
    }

    await Promise.all(tasks);
  }

  private observeForLazy(para: Paragraph): void {
    if (!this.viewportObserver) {
      this.viewportObserver = this.ctx.createViewportObserver({
        offset: this.config.lazyOffset ?? 0,
      });
    }

    const anchor = para.commonAncestor;
    if (anchor && anchor.nodeType === Node.ELEMENT_NODE) {
      this.viewportObserver.observe(anchor as Element, () => {
        this.translateParagraph(para);
      });
    }
  }

  private observeHidden(para: Paragraph, hiddenNodes: Text[]): void {
    const ancestors = getHiddenAncestors(hiddenNodes);

    if (!this.viewportObserver) {
      this.viewportObserver = this.ctx.createViewportObserver({
        offset: this.config.lazyOffset ?? 0,
      });
    }

    for (const [ancestor] of ancestors) {
      this.viewportObserver.observe(ancestor, () => {
        this.translateParagraph(para);
      });
    }
  }

  private async translateParagraph(para: Paragraph): Promise<void> {
    if (this.destroyed) return;
    const text = para.text;
    if (normalizeWhitespace(text).length === 0) return;

    const srcLang = this.config.srcLanguage ?? 'auto';
    const tgtLang = this.config.tgtLanguage;
    const sourceHash = hashString(normalizeWhitespace(text));
    const anchor = this.getInsertionPoint(para);
    const onTranslatingStart = this.config.onTranslatingStart;
    const onTranslatingEnd = this.config.onTranslatingEnd;
    const mutator = this.createMutator(sourceHash);

    if (this.translatedParagraphs.has(sourceHash) || this.pendingTranslations.has(sourceHash)) return;

    this.pendingTranslations.add(sourceHash);

    try {
      if (onTranslatingStart) {
        const startCtx: TranslatingContext = {
          paragraph: para,
          sourceHash,
          anchor,
          mutator,
        };
        await Promise.resolve(onTranslatingStart(startCtx));
      }

      if (this.destroyed) return;

      const htmlContent = text2HTML(text);
      const translation = await this.ctx.queue.submit(htmlContent, srcLang, tgtLang, 'html');
      if (this.destroyed) return;
      const translatedText = html2Text(translation);

      if (translatedText && normalizeWhitespace(translatedText) !== normalizeWhitespace(text)) {
        this.pauseDetector();
        await this.insertTranslation(para, translatedText, sourceHash);
        this.resumeDetector();
      }
    } catch (e) {
      console.error('[ParagraphComparePlugin] Translation failed:', e);
    } finally {
      if (onTranslatingEnd) {
        const endCtx: TranslatingContext = {
          paragraph: para,
          sourceHash,
          anchor,
          mutator,
        };
        await Promise.resolve(onTranslatingEnd(endCtx));
      }
      this.pendingTranslations.delete(sourceHash);
    }
  }

  private async insertTranslation(
    para: Paragraph,
    translatedText: string,
    sourceHash: string,
  ): Promise<void> {
    if (this.translatedParagraphs.has(sourceHash)) return;

    const rules = this.config.rules;
    const onBefore = this.config.onBeforeInsertTrans;
    const mutator = this.createMutator(sourceHash);


    const transNode = createTransNode();
    transNode.setAttribute('data-source-hash', sourceHash);
    const isInline = this.isInlineParagraph(para);

    if (isInline) {
      transNode.style.display = 'inline';
      transNode.textContent = `\u00A0\u00A0${translatedText}`;
    } else {
      transNode.style.display = 'block';
      const br = document.createElement('br');
      transNode.appendChild(br);
      transNode.appendChild(document.createTextNode(translatedText));
    }
    const anchor = this.getInsertionPoint(para);

    if (Array.isArray(rules) && rules.length > 0) {
      this.applyRules(transNode, para, rules, sourceHash);
    }

    const ctx: TransInsertContext = {
      paragraph: para,
      translatedText,
      transNode,
      anchor,
      sourceHash,
      mutator,
    };

    let finalNodeToInsert: HTMLElement = transNode;
    if (onBefore) {
      const result = await Promise.resolve(onBefore(ctx));
      if (result instanceof HTMLElement) {
        finalNodeToInsert = result;
      }
    }

    const parent = anchor?.parentNode ?? null;
    if (parent) {
      if (this.translatedParagraphs.has(sourceHash)) return;
      parent.insertBefore(finalNodeToInsert, (anchor as Node).nextSibling);
      this.translatedParagraphs.set(sourceHash, finalNodeToInsert);
    }

  }

  /** 遍历每条 rule 从 commonAncestor 向上找首个匹配的节点并应用：transStyle 到 trans，style/className 到该节点；多条 rule 均可生效 */
  private applyRules(
    transNode: HTMLElement,
    para: Paragraph,
    rules: NonNullable<ParagraphTranslateConfig['rules']>,
    sourceHash: string,
  ): void {
    let start: Element | null =
      para.commonAncestor.nodeType === Node.ELEMENT_NODE
        ? (para.commonAncestor as Element)
        : para.commonAncestor.parentElement;

    for (const rule of rules) {
      let el: Element | null = start;
      while (el) {
        if (el.matches(rule.selector)) {
          if (rule.transStyle) {
            for (const [key, value] of Object.entries(rule.transStyle)) {
              transNode.style.setProperty(key, value);
            }
          }
          if (rule.style) {
            const container = el as HTMLElement;
            for (const [key, value] of Object.entries(rule.style)) {
              this.trackStyleRollback(sourceHash, container, key);
              container.style.setProperty(key, value);
            }
          }
          if (rule.className) {
            this.trackClassRollback(sourceHash, el as HTMLElement, rule.className);
            (el as HTMLElement).classList.add(rule.className);
          }
          break;
        }
        el = el.parentElement;
      }
    }
  }

  private isInlineParagraph(para: Paragraph): boolean {
    if (matchesParagraphSelectors(para, this.config.extraBlockSelectors)) return false;
    if (matchesParagraphSelectors(para, this.config.extraInlineSelectors)) return true;

    if (para.display !== 'block' && para.display !== 'flex') return true;

    const text = para.text;
    const hasChineseJapanese = /[\u4e00-\u9fff\u3040-\u309f\u30a0-\u30ff]/.test(text);
    if (hasChineseJapanese && text.length < 20) return true;

    return false;
  }

  private getInsertionPoint(para: Paragraph): Node | null {
    const rootNodes = para.rootNodes;
    return rootNodes[rootNodes.length - 1] ?? null;
  }

  private isAlreadyTranslated(para: Paragraph): boolean {
    const sourceHash = hashString(normalizeWhitespace(para.text));
    return this.translatedParagraphs.has(sourceHash) || this.pendingTranslations.has(sourceHash);
  }

  private startDynamicDetection(): void {
    const targets = this.getTargets();

    for (const target of targets) {
      this.domChangeDetector = this.ctx.createDOMChangeDetector({
        target,
        ignoreSelector: 'trans, [alitranslate="no"]',
        debounceMs: 300,
        onDetect: (records) => this.handleDOMChanges(records),
      });
      this.domChangeDetector.start();
    }
  }

  private handleDOMChanges(records: MutationRecord[]): void {
    if (this.destroyed) return;
    let hasRealChanges = false;

    for (const record of records) {
      if (record.type === 'childList') {
        for (const removed of Array.from(record.removedNodes)) {
          if (removed.nodeType === Node.ELEMENT_NODE) {
            const el = removed as Element;
            if (el.tagName === TRANS_NODE_TAG) continue;
            // loading / 临时占位节点不触发翻译，避免 onTranslatingStart 循环
            if (el.getAttribute('alitranslate') === 'no') continue;
          }
          hasRealChanges = true;
        }

        for (const added of Array.from(record.addedNodes)) {
          if (added.nodeType === Node.ELEMENT_NODE) {
            const el = added as Element;
            if (el.tagName === TRANS_NODE_TAG) continue;
            if (el.getAttribute('alitranslate') === 'no') continue;
          }
          hasRealChanges = true;
        }
      }

      if (record.type === 'characterData') {
        const target = record.target;
        let next: Node | null = target.nextSibling;
        if (next && next.nodeType === Node.ELEMENT_NODE) {
          const nextEl = next as Element;
          const hash = nextEl.getAttribute('data-source-hash');
          if (hash && nextEl.tagName === TRANS_NODE_TAG) {
            this.translatedParagraphs.delete(hash);
            nextEl.remove();
            this.rollbackBySourceHash(hash);
          }
        }
        if (!next || next.nodeType !== Node.ELEMENT_NODE) {
          let el: Node | null = target.parentElement;
          while (el) {
            const sib = el.nextSibling;
            if (sib && sib.nodeType === Node.ELEMENT_NODE) {
              const sibEl = sib as Element;
              const hash = sibEl.getAttribute('data-source-hash');
              if (hash && sibEl.tagName === TRANS_NODE_TAG) {
                this.translatedParagraphs.delete(hash);
                sibEl.remove();
                this.rollbackBySourceHash(hash);
                break;
              }
            }
            el = el.parentNode;
          }
        }
        hasRealChanges = true;
      }
    }

    if (hasRealChanges) {
      this.retranslateChanged();
    }
  }

  private async retranslateChanged(): Promise<void> {
    this.cleanupStaleTransNodes();

    const targets = this.getTargets();
    const tasks: Promise<void>[] = [];

    for (const target of targets) {
      const excludeSelector = this.buildExcludeSelector();
      const paragraphs = getParagraphs(target, excludeSelector);

      for (const para of paragraphs) {
        if (this.isAlreadyTranslated(para)) continue;

        if (this.config.lazyload) {
          this.observeForLazy(para);
        } else {
          tasks.push(this.translateParagraph(para));
        }
      }
    }

    await Promise.all(tasks);
  }

  private cleanupStaleTransNodes(): void {
    const targets = this.getTargets();
    const excludeSelector = this.buildExcludeSelector();

    for (const target of targets) {
      const paragraphs = getParagraphs(target, excludeSelector);
      const currentHashes = new Set(
        paragraphs.map(p => hashString(normalizeWhitespace(p.text))),
      );

      const transNodes = target.querySelectorAll(TRANS_NODE_TAG);
      for (const transNode of Array.from(transNodes)) {
        const hash = transNode.getAttribute('data-source-hash');
        if (hash && !currentHashes.has(hash)) {
          this.translatedParagraphs.delete(hash);
          transNode.remove();
          this.rollbackBySourceHash(hash);
        }
      }
    }
  }

  private pauseDetector(): void {
    this.domChangeDetector?.pause();
  }

  private resumeDetector(): void {
    this.domChangeDetector?.resume();
  }

  private getTargets(): Element[] {
    const selectors = this.config.targetSelectors ?? ['body'];
    const selectorList = Array.isArray(selectors) ? selectors : [selectors];
    const excludeSelectors = this.config.excludeSelectors ?? [];
    const excludeList = Array.isArray(excludeSelectors) ? excludeSelectors : [excludeSelectors];

    const targets: Element[] = [];
    for (const selector of selectorList) {
      const elements = document.querySelectorAll(selector);
      elements.forEach(el => {
        const isExcluded = excludeList.some(ex => el.matches(ex));
        if (!isExcluded) targets.push(el);
      });
    }

    return targets.length > 0 ? targets : [document.body];
  }

  private buildExcludeSelector(): string | undefined {
    const excludes = this.config.excludeSelectors;
    if (!excludes) return undefined;
    const list = Array.isArray(excludes) ? excludes : [excludes];
    return list.join(', ') || undefined;
  }

  destroy(): void {
    this.destroyed = true;
    this.viewportObserver?.destroy();
    this.viewportObserver = null;
    this.domChangeDetector?.destroy();
    this.domChangeDetector = null;

    this.remove();
  }

  remove(): void {
    if (!this.initialized) return;
    this.viewportObserver?.destroy();
    this.viewportObserver = null;
    this.domChangeDetector?.destroy();
    this.domChangeDetector = null;
    for (const [hash, transNode] of this.translatedParagraphs) {
      transNode.remove();
      this.rollbackBySourceHash(hash);
    }
    this.translatedParagraphs.clear();
    this.pendingTranslations.clear();
  }

  private createMutator(sourceHash: string): RevertableMutator {
    return {
      setStyle: (el, prop, value, priority) => {
        this.trackStyleRollback(sourceHash, el, prop);
        el.style.setProperty(prop, value, priority ?? '');
      },
      addClass: (el, className) => {
        this.trackClassRollback(sourceHash, el, className);
        el.classList.add(className);
      },
      removeClass: (el, className) => {
        const hadClass = el.classList.contains(className);
        this.pushRollback(sourceHash, () => {
          if (hadClass) {
            el.classList.add(className);
          } else {
            el.classList.remove(className);
          }
        });
        el.classList.remove(className);
      },
      appendNode: (parent, node) => {
        const nextSibling = node.nextSibling;
        parent.appendChild(node);
        // onTranslatingStart 插入的临时节点应不参与翻译（dom.ts 的 needTranslate 会检查该属性）
        if (node.nodeType === Node.ELEMENT_NODE) {
          (node as Element).setAttribute('alitranslate', 'no');
        }
        this.pushRollback(sourceHash, () => {
          if (node.parentNode === parent) {
            parent.removeChild(node);
          } else if (nextSibling) {
            parent.insertBefore(node, nextSibling);
          } else {
            parent.appendChild(node);
          }
        });
      },
      removeNode: (node) => {
        const parent = node.parentNode;
        const nextSibling = node.nextSibling;
        if (!parent) return;
        parent.removeChild(node);
        this.pushRollback(sourceHash, () => {
          if (nextSibling && nextSibling.parentNode === parent) {
            parent.insertBefore(node, nextSibling);
          } else {
            parent.appendChild(node);
          }
        });
      },
    };
  }

  private pushRollback(sourceHash: string, fn: () => void): void {
    const stack = this.rollbackStacks.get(sourceHash) ?? [];
    stack.push(fn);
    this.rollbackStacks.set(sourceHash, stack);
  }

  private trackStyleRollback(sourceHash: string, el: HTMLElement, prop: string): void {
    const prevValue = el.style.getPropertyValue(prop);
    const prevPriority = el.style.getPropertyPriority(prop);
    const hadInline = prevValue !== '';
    this.pushRollback(sourceHash, () => {
      if (!hadInline) {
        el.style.removeProperty(prop);
        return;
      }
      el.style.setProperty(prop, prevValue, prevPriority);
    });
  }

  private trackClassRollback(sourceHash: string, el: HTMLElement, className: string): void {
    const hadClass = el.classList.contains(className);
    this.pushRollback(sourceHash, () => {
      if (!hadClass) {
        el.classList.remove(className);
      }
    });
  }

  private rollbackBySourceHash(sourceHash: string): void {
    const stack = this.rollbackStacks.get(sourceHash);
    if (!stack || stack.length === 0) return;
    for (let i = stack.length - 1; i >= 0; i--) {
      try {
        stack[i]();
      } catch (e) {
        console.error('[ParagraphComparePlugin] rollback failed:', e);
      }
    }
    this.rollbackStacks.delete(sourceHash);
  }
}
